import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "./AcademicCreate.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomRadioGroup,
  CustomTextInput,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";

const AcademicCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const editId = location.state?.ID || null;

  // An academic year cannot start in the past: only the current year onwards
  // is selectable, for both the year inputs and the date pickers.
  const currentYear = dayjs().year();
  const minSelectableDate = dayjs().startOf("year");

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    startYear: "",
    endYear: "",
    fromDate: null,
    toDate: null,
    is_upcoming_year: "0",
    isActive: "",
  });
  const [errors, setErrors] = useState({});

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const UPCOMING_YEAR_OPTIONS = [
    { value: "1", label: translations.labels?.yes || "Yes", disabled: false },
    { value: "0", label: translations.labels?.no || "No", disabled: false },
  ];

  const STATUS_OPTIONS = [
    {
      value: "1",
      label: translations.labels?.active || "Active",
      disabled: false,
    },
    {
      value: "0",
      label: translations.labels?.inactive || "Inactive",
      disabled: false,
    },
  ];

  useEffect(() => {
    if (editId) {
      fetchAcademicDetails();
    }
  }, [editId]);

  const fetchAcademicDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/academicView/${editId}`,
      );

      if (response?.data?.success) {
        const data = response.data.data[0];

        let startYear = "";
        let endYear = "";

        if (data.academic_year) {
          const years = data.academic_year.split("-").map((y) => y.trim());
          startYear = years[0] || "";
          endYear = years[1] || "";
        }

        setFormData({
          startYear: startYear,
          endYear: endYear,
          fromDate: data.start_date
            ? dayjs(data.start_date).format("YYYY-MM-DD")
            : null,
          toDate: data.end_date
            ? dayjs(data.end_date).format("YYYY-MM-DD")
            : null,
          is_upcoming_year: data.is_upcoming_year?.toString() || "0",
          isActive:
            data.isActive !== undefined && data.isActive !== null
              ? data.isActive.toString()
              : "",
        });
      }
    } catch (error) {
      console.error("Error fetching academic details:", error);
      alert("Failed to fetch academic details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "startYear" || name === "endYear") {
      const numericValue = value.replace(/\D/g, "").slice(0, 4);
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

      // Reject a past year as soon as a complete 4-digit year is entered.
      // Shorter values are still in progress, so they are left alone.
      const pastYearError =
        numericValue.length === 4 && parseInt(numericValue, 10) < currentYear
          ? translations.labels?.yearNotInPast ||
            `Year must be ${currentYear} or later`
          : "";
      setErrors((prev) => ({
        ...prev,
        [name]: pastYearError,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : null,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.startYear) {
      newErrors.startYear =
        translations.labels?.startYearRequired || "Start Year is required";
    } else if (!/^\d{4}$/.test(formData.startYear)) {
      newErrors.startYear =
        translations.labels?.startYearInvalid ||
        "Please enter a valid year (e.g., 2025)";
    } else if (parseInt(formData.startYear, 10) < currentYear) {
      newErrors.startYear =
        translations.labels?.startYearNotInPast ||
        `Start Year must be ${currentYear} or later`;
    }

    if (!formData.endYear) {
      newErrors.endYear =
        translations.labels?.endYearRequired || "End Year is required";
    } else if (!/^\d{4}$/.test(formData.endYear)) {
      newErrors.endYear =
        translations.labels?.endYearInvalid ||
        "Please enter a valid year (e.g., 2026)";
    } else if (parseInt(formData.endYear, 10) < currentYear) {
      newErrors.endYear =
        translations.labels?.endYearNotInPast ||
        `End Year must be ${currentYear} or later`;
    }

    if (
      formData.startYear &&
      formData.endYear &&
      parseInt(formData.endYear) <= parseInt(formData.startYear)
    ) {
      newErrors.endYear =
        translations.labels?.endYearGreater ||
        "End Year must be greater than Start Year";
    }

    if (!formData.fromDate) {
      newErrors.fromDate =
        translations.labels?.fromDateRequired || "From Date is required";
    } else if (dayjs(formData.fromDate).year() < currentYear) {
      newErrors.fromDate =
        translations.labels?.fromDateNotInPast ||
        `From Date cannot be earlier than ${currentYear}`;
    } else if (formData.startYear && /^\d{4}$/.test(formData.startYear)) {
      const fromDateYear = dayjs(formData.fromDate).year();
      const startYear = parseInt(formData.startYear);

      if (fromDateYear !== startYear) {
        newErrors.fromDate =
          translations.labels?.fromDateYearMismatch ||
          `From Date must be in the year ${formData.startYear}`;
      }
    }

    if (!formData.toDate) {
      newErrors.toDate =
        translations.labels?.toDateRequired || "To Date is required";
    } else if (dayjs(formData.toDate).year() < currentYear) {
      newErrors.toDate =
        translations.labels?.toDateNotInPast ||
        `To Date cannot be earlier than ${currentYear}`;
    } else if (formData.endYear && /^\d{4}$/.test(formData.endYear)) {
      const toDateYear = dayjs(formData.toDate).year();
      const endYear = parseInt(formData.endYear);

      if (toDateYear !== endYear) {
        newErrors.toDate =
          translations.labels?.toDateYearMismatch ||
          `To Date must be in the year ${formData.endYear}`;
      }
    }

    if (
      formData.fromDate &&
      formData.toDate &&
      dayjs(formData.toDate).isBefore(dayjs(formData.fromDate))
    ) {
      newErrors.toDate =
        translations.labels?.toDateAfter || "To Date must be after From Date";
    }

    if (!formData.is_upcoming_year) {
      newErrors.is_upcoming_year =
        translations.labels?.isUpcomingYearRequired ||
        "Is This Upcoming Year is required";
    }

    if (!formData.isActive) {
      newErrors.isActive =
        translations.labels?.isActiveRequired || "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        client_id: client_id,
        school_id: school_id,
        academic_year_start: formData.startYear,
        academic_year_end: formData.endYear,
        start_date: formData.fromDate,
        end_date: formData.toDate,
        is_upcoming_year: parseInt(formData.is_upcoming_year),
        isActive: parseInt(formData.isActive),
        created_by: user_id,
        created_dt: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      let response;

      if (editId) {
        payload.id = editId;
        response = await AxiosInstance.put("/school/academiCreate", payload);
      } else {
        response = await AxiosInstance.post("/school/academiCreate", payload);
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.academicUpdatedSuccess ||
              "Academic configuration updated successfully!"
            : translations.labels?.academicCreatedSuccess ||
              "Academic configuration created successfully!",
          redirectPath: "/user/academicList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.somethingWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.academicSaveFailed ||
          "Failed to save academic configuration. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/academicList");
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.academicConfiguration ||
              "Academic Configuration"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.academicConfiguration ||
              "Academic Configuration"}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.startYear || "Start Year"}
                name="startYear"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={formData.startYear}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.startYearPlaceholder || "E.g. 2025"
                }
                required
                error={errors.startYear}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.endYear || "End Year"}
                name="endYear"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={formData.endYear}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.endYearPlaceholder || "E.g. 2026"
                }
                required
                error={errors.endYear}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.fromDate || "From Date"}
                name="fromDate"
                value={formData.fromDate ? dayjs(formData.fromDate) : null}
                onChange={(date) => handleDateChange("fromDate", date)}
                placeholder={translations.labels?.selectDate || "Select Date"}
                minDate={minSelectableDate}
                required
                error={errors.fromDate}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.toDate || "To Date"}
                name="toDate"
                value={formData.toDate ? dayjs(formData.toDate) : null}
                onChange={(date) => handleDateChange("toDate", date)}
                placeholder={translations.labels?.selectDate || "Select Date"}
                minDate={minSelectableDate}
                required
                error={errors.toDate}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  translations.labels?.isUpcomingYear ||
                  "Is This Upcoming Year?"
                }
                name="is_upcoming_year"
                value={formData.is_upcoming_year}
                onChange={(value) =>
                  handleRadioChange("is_upcoming_year", value)
                }
                options={UPCOMING_YEAR_OPTIONS}
                required
                error={errors.is_upcoming_year}
              />
            </div>

            {/* Status - Active / Inactive */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={translations.labels?.status || "Status"}
                name="isActive"
                value={formData.isActive}
                onChange={(value) => handleRadioChange("isActive", value)}
                options={STATUS_OPTIONS}
                required
                error={errors.isActive}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {editId
                ? translations.labels?.update || "UPDATE"
                : translations.labels?.submit || "SUBMIT"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AcademicCreate;
